import {
  Color,
  type Data3DTexture,
  DepthTexture,
  HalfFloatType,
  Mesh,
  NearestFilter,
  OrthographicCamera,
  PlaneGeometry,
  type RawShaderMaterial,
  RGBAFormat,
  Scene,
  UnsignedByteType,
  UnsignedIntType,
  Vector2,
  WebGLRenderTarget,
  type WebGLRenderer,
} from 'three';
import { type PixelCamera, TARGET_H, TARGET_W, VIEW_H, VIEW_W } from '../camera/PixelCamera';
import { sharedUniforms } from '../materials/toon';
import type { Grade } from '../palette';
import {
  effectiveGrade, NEUTRAL_OPTIONS, type PipelineOptions, postOptionUniforms, upscaleOptionUniforms,
} from './options';
import { createPostMaterial, createUpscaleMaterial } from './passes';

export type { PipelineOptions } from './options';

export interface PipelineSettings {
  paletteLock: number;
  ditherAmp: number;
  outlines: boolean;
  vignette: number;
}

const postTargetOptions = {
  type: UnsignedByteType,
  format: RGBAFormat,
  minFilter: NearestFilter,
  magFilter: NearestFilter,
  depthBuffer: false,
  generateMipmaps: false,
} as const;

/**
 * The frame (05 §8): scene -> [colour + normal/id + depth] at 642x362 -> outline, haze,
 * palette lock, grade -> integer nearest-neighbour upscale with sub-texel scroll.
 * The shadow pass is three.js's own and runs inside the scene render.
 */
export class RenderPipeline {
  readonly settings: PipelineSettings = {
    paletteLock: 0.85,
    ditherAmp: 0.07,
    outlines: true,
    vignette: 0.18,
  };

  private readonly sceneTarget: WebGLRenderTarget;
  /** The finished low-resolution frame. With the flash cap on, two take turns: one is drawn, the other is "last frame". */
  private postTarget: WebGLRenderTarget;
  private previousTarget: WebGLRenderTarget;
  private readonly postMaterial: RawShaderMaterial;
  private readonly upscaleMaterial: RawShaderMaterial;
  private readonly quadScene = new Scene();
  private readonly quadCamera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly quad: Mesh;
  private readonly clearColor = new Color();
  private readonly bufferSize = new Vector2();
  private readonly lastCamTexel = new Vector2();
  /** False until a frame has been drawn with the flash cap on: there is no "last frame" to compare with yet. */
  private previousValid = false;
  private scale = 1;
  private options: PipelineOptions = { ...NEUTRAL_OPTIONS };
  private grade: Grade | null = null;
  private hazeHex = '#ffffff';
  /** Where the game image sits on the page, CSS pixels; input and the HUD both need it. */
  readonly viewRect = { left: 0, top: 0, width: VIEW_W, height: VIEW_H };

  constructor(
    private readonly renderer: WebGLRenderer,
    lut: Data3DTexture,
    ink: Color,
  ) {
    this.sceneTarget = new WebGLRenderTarget(TARGET_W, TARGET_H, {
      count: 2,
      type: HalfFloatType,
      format: RGBAFormat,
      minFilter: NearestFilter,
      magFilter: NearestFilter,
      depthBuffer: true,
      generateMipmaps: false,
    });
    const depthTexture = new DepthTexture(TARGET_W, TARGET_H);
    depthTexture.type = UnsignedIntType;
    depthTexture.minFilter = NearestFilter;
    depthTexture.magFilter = NearestFilter;
    this.sceneTarget.depthTexture = depthTexture;

    this.postTarget = new WebGLRenderTarget(TARGET_W, TARGET_H, postTargetOptions);
    this.previousTarget = new WebGLRenderTarget(TARGET_W, TARGET_H, postTargetOptions);

    const [color, normalId] = this.sceneTarget.textures;
    if (!color || !normalId) throw new Error('scene target has no attachments');
    const previous = this.previousTarget.texture;
    this.postMaterial = createPostMaterial({ color, normalId, depth: depthTexture, lut, previous, ink });
    this.upscaleMaterial = createUpscaleMaterial(this.postTarget.texture);

    this.quad = new Mesh(new PlaneGeometry(2, 2), this.postMaterial);
    this.quad.frustumCulled = false;
    this.quadScene.add(this.quad);
  }

  /** Integer scale currently used for the blow-up (for the dev overlay and screenshots). */
  get integerScale(): number {
    return this.scale;
  }

  setLut(lut: Data3DTexture): void {
    this.uniform(this.postMaterial, 'tLut').value = lut;
  }

  /** Light rim colour for the player at night; pass null for the normal ink outline. */
  setPlayerRim(hex: string | null): void {
    const post = this.postMaterial;
    if (hex === null) {
      this.uniform(post, 'uPlayerRim').value = this.uniform(post, 'uInk').value;
      this.uniform(post, 'uPlayerRimAfterGrade').value = 0;
      return;
    }
    const c = new Color(hex).convertLinearToSRGB();
    this.uniform(post, 'uPlayerRim').value = [c.r, c.g, c.b];
    this.uniform(post, 'uPlayerRimAfterGrade').value = 1;
  }

  /** Post-pass pixels (RGBA8, bottom row first, border included): for automated look checks. */
  readPixels(): Uint8Array {
    const out = new Uint8Array(TARGET_W * TARGET_H * 4);
    this.renderer.readRenderTargetPixels(this.postTarget, 0, 0, TARGET_W, TARGET_H, out);
    return out;
  }

  /** Haze colour and the post-quantise grade come from the time-of-day grade (05 §7). */
  setGrade(grade: Grade, hazeHex: string): void {
    this.grade = grade;
    this.hazeHex = hazeHex;
    this.applyGrade();
  }

  /**
   * Post-lock looks (see `PipelineOptions`): NVG, the Night Eyes share of the night grade, the CRT
   * finish, the flash cap. Give only what changes; everything starts, and can be put back, neutral.
   */
  setOptions(options: Partial<PipelineOptions>): void {
    const wasCapped = this.options.flashCap;
    this.options = { ...this.options, ...options };
    if (this.options.flashCap && !wasCapped) this.previousValid = false;
    this.applyGrade();
    const post = postOptionUniforms(this.options);
    this.uniform(this.postMaterial, 'uNvg').value = post.uNvg;
  }

  getOptions(): Readonly<PipelineOptions> {
    return this.options;
  }

  /** Current value of a post-pass or upscale uniform, for tests and the debug overlay. */
  uniformValue(pass: 'post' | 'upscale', name: string): unknown {
    return this.uniform(pass === 'post' ? this.postMaterial : this.upscaleMaterial, name).value;
  }

  private applyGrade(): void {
    if (!this.grade) return;
    const grade = effectiveGrade(this.grade, this.options);
    const post = this.postMaterial;
    (this.uniform(post, 'uHaze').value as Color).set(this.hazeHex);
    this.uniform(post, 'uHazeAmount').value = grade.hazeAmount;
    const tint = new Color(grade.tint).convertLinearToSRGB();
    this.uniform(post, 'uTint').value = [tint.r, tint.g, tint.b];
    this.uniform(post, 'uTintAmount').value = grade.tintAmount;
    this.uniform(post, 'uValueScale').value = grade.valueScale;
    // Sky behind everything: haze colour, slightly darkened by the grade's value scale.
    this.clearColor.set(this.hazeHex).multiplyScalar(grade.valueScale);
  }

  render(scene: Scene, pixelCamera: PixelCamera): void {
    const renderer = this.renderer;
    const camera = pixelCamera.camera;
    sharedUniforms.uCamTexel.value.copy(pixelCamera.camTexel);

    // 1-2: shadow pass + scene pass into colour / normal+id / depth.
    renderer.setRenderTarget(this.sceneTarget);
    renderer.setClearColor(this.clearColor, 0);
    renderer.clear(true, true, true);
    renderer.render(scene, camera);

    // 3-5: outlines, haze, palette lock, grade - still at internal resolution.
    const post = this.postMaterial;
    this.uniform(post, 'uNear').value = camera.near;
    this.uniform(post, 'uFar').value = camera.far;
    this.uniform(post, 'uDepthMid').value = camera.position.distanceTo(pixelCamera.focus);
    this.uniform(post, 'uDepthSpan').value = pixelCamera.viewMetres.depth * pixelCamera.cosPitch;
    this.uniform(post, 'uPaletteLock').value = this.settings.paletteLock;
    this.uniform(post, 'uDitherAmp').value = this.settings.ditherAmp;
    this.uniform(post, 'uOutlines').value = this.settings.outlines ? 1 : 0;
    this.prepareFlashCap(pixelCamera);
    this.quad.material = post;
    renderer.setRenderTarget(this.postTarget);
    renderer.render(this.quadScene, this.quadCamera);

    // 7: integer upscale to the canvas, letterboxed.
    const up = this.upscaleMaterial;
    this.uniform(up, 'tImage').value = this.postTarget.texture;
    (this.uniform(up, 'uSubTexel').value as Vector2).copy(pixelCamera.subTexel);
    this.uniform(up, 'uVignette').value = this.settings.vignette;
    this.quad.material = up;
    renderer.setRenderTarget(null);
    renderer.getDrawingBufferSize(this.bufferSize);
    const bw = this.bufferSize.x;
    const bh = this.bufferSize.y;
    this.scale = Math.max(1, Math.floor(Math.min(bw / VIEW_W, bh / VIEW_H)));
    const finish = upscaleOptionUniforms(this.options, this.scale);
    this.uniform(up, 'uScale').value = this.scale;
    this.uniform(up, 'uScanline').value = finish.uScanline;
    this.uniform(up, 'uVignetteBoost').value = finish.uVignetteBoost;
    const w = VIEW_W * this.scale;
    const h = VIEW_H * this.scale;
    const pr = renderer.getPixelRatio();
    renderer.setClearColor(0x14121a, 1);
    renderer.setViewport(0, 0, bw / pr, bh / pr);
    renderer.clear(true, false, false);
    const left = Math.floor((bw - w) / 2) / pr;
    const bottom = Math.floor((bh - h) / 2) / pr;
    renderer.setViewport(left, bottom, w / pr, h / pr);
    this.viewRect.left = left;
    this.viewRect.top = bh / pr - bottom - h / pr;
    this.viewRect.width = w / pr;
    this.viewRect.height = h / pr;
    renderer.render(this.quadScene, this.quadCamera);
  }

  /**
   * With the flash cap on, the frame just shown becomes "last frame" and the other target is drawn
   * into, so the post pass never reads the texture it is writing. Off, nothing swaps and nothing is read.
   */
  private prepareFlashCap(pixelCamera: PixelCamera): void {
    const post = this.postMaterial;
    const cap = postOptionUniforms(this.options).uFlashCap;
    if (cap <= 0) {
      this.uniform(post, 'uFlashCap').value = 0;
      return;
    }
    const shown = this.postTarget;
    this.postTarget = this.previousTarget;
    this.previousTarget = shown;
    this.uniform(post, 'tPrev').value = this.previousTarget.texture;
    (this.uniform(post, 'uPrevShift').value as Vector2).subVectors(pixelCamera.camTexel, this.lastCamTexel);
    this.uniform(post, 'uFlashCap').value = this.previousValid ? cap : 0;
    this.lastCamTexel.copy(pixelCamera.camTexel);
    this.previousValid = true;
  }

  /** Free GPU resources when a mission ends. */
  dispose(): void {
    this.sceneTarget.dispose();
    this.postTarget.dispose();
    this.previousTarget.dispose();
    this.postMaterial.dispose();
    this.upscaleMaterial.dispose();
    this.quad.geometry.dispose();
  }

  private uniform(material: RawShaderMaterial, name: string): { value: unknown } {
    const u = material.uniforms[name];
    if (!u) throw new Error(`missing uniform ${name}`);
    return u;
  }
}
